
import { PLACEHOLDER_DATA_URI } from '@/assets/placeholder'
import BarcodeScanner from '@/components/common/BarcodeScanner.vue'
import { buildSpecCategories } from '@/utils/specFieldMap'
import { matchSpecCategory, specResultQuery } from '@/utils/searchEntries'

const HISTORY_KEY = 'search_history_v2'
const MAX_HISTORY = 10
// 规格选项默认展示数（移动端约 5 行；此前 4 个≈1 行，减少"更多"展开操作）
const DEFAULT_VISIBLE_OPTIONS = 20

export default {
  name: 'Search',
  components: { BarcodeScanner },
  data () {
    return {
      // 搜索相关
      realValue: '',
      isRemove: false,
      entrySource: '', // 入口来源：direct_search(首页搜索框)/suggestion(推荐词)/product_tag(详情标签)/spec_filter(规格筛选)——决定 focus 策略
      suggestions: [], // 联想词（/search/suggest 三来源合并）
      showSuggest: false,
      placeholder: '搜商品 / 品牌 / 规格 / 型号',
      placeholderKeyword: '', // 首页推荐词（仅作 placeholder，不填入 input）
      placeholderImg: PLACEHOLDER_DATA_URI,
      // 扫码
      showScanner: false,
      specFieldExpanded: {},
      historyWord: [],
      hotWord: [],

      // Tab 相关
      currentTab: 'search',
      tabs: [
        { key: 'search', label: '搜索' },
        { key: 'spec', label: '按规格' },
        { key: 'car', label: '按车型' }
      ],

      // 猜你想搜 - 高频采购入口（只留一排4个，去掉雨刷/刹车片/滤清器/刹车油）。
      // 维保四大件全是类目入口：category 指向 SPEC_FIELD_MAP 类目 key，
      // 点击进规格筛选上下文（类目默认全量，结果页可按规格/车型 facet 收窄）
      hotParts: [
        { key: 'tire', name: '轮胎', category: 'tire', icon: '/icons/tire.svg' },
        { key: 'battery', name: '电瓶', category: 'battery', icon: '/icons/battery.svg' },
        { key: 'oil', name: '机油', category: 'oil', icon: '/icons/oil.svg' },
        { key: 'spark', name: '火花塞', category: 'spark_plug', icon: '/icons/spark.svg' }
      ],

      // 按规格相关
      selectedCategory: null,
      specParams: {},
      categories: buildSpecCategories(),
      // 规格标签命中状态（布尔，/search/tag-stats）：category → specKey → value → true
      // 键序=命中数降序（展示顺序）；无数量（管理统计能力不进用户端）；
      // tagStatsLoaded=false（未到/失败）时保守全展示，true 后无键=0命中=不渲染
      tagStats: {},
      tagStatsLoaded: false,
      // 模板可见的默认展示数（Vue2 模板不达模块常量）
      defaultVisibleOptions: DEFAULT_VISIBLE_OPTIONS,

      // 按车型相关 - 二轮车型号列表（按品牌分类）
      selectedCarType: null,
      carTypes: [], // 从后端 product_tags(tag_type=model) 动态加载，不再硬编码
      vehicleCategories: [] // 车型分类列表（从 _categories 读取）
    }
  },
  computed: {
    indicatorStyle () {
      const count = this.tabs.length || 1
      const index = this.tabs.findIndex(t => t.key === this.currentTab)
      return {
        width: `${100 / count}%`,
        transform: `translateX(${index * 100}%)`
      }
    },
    currentSpecFields () {
      return this.selectedCategory?.fields || []
    },
    // 摩托车按品牌分组
    motorcycleByBrand () {
      return this.groupByBrand('motorcycle')
    },
    // 电动车按品牌分组
    ebikeByBrand () {
      return this.groupByBrand('ebike')
    },
    // 按分类分组车型（从后端 category_id 动态映射，不再硬编码）
    carTypesByCategory () {
      const grouped = {}
      this.carTypes.forEach(t => {
        const cat = t.category || '摩托车'
        if (!grouped[cat]) grouped[cat] = []
        grouped[cat].push(t)
      })
      return grouped
    },
    // 按 vehicle_categories sort_order 排序的分类名列表
    vehicleCategoryNames () {
      const ordered = this.vehicleCategories
        .filter(c => c.is_active !== 0)
        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
        .map(c => c.name)
      // 补上未在 categories 表中的分类名（fallback 场景）
      const known = new Set(ordered)
      Object.keys(this.carTypesByCategory).forEach(name => {
        if (!known.has(name)) ordered.push(name)
      })
      return ordered
    }
  },
  async mounted () {
    // 入口来源与 placeholder（SEARCH_ENTRY 规则）：
    // placeholder 只作提示，绝不写入 input value / 搜索历史 / query keyword；
    // 旧入口的 query.keyword 同样只当提示消费（兼容，不锁定输入框）
    const q = this.$route.query
    this.entrySource = q.source || ''
    const ph = q.placeholder || q.keyword
    if (ph && typeof ph === 'string' && ph.trim()) {
      this.placeholderKeyword = ph.trim()
    }
    // 移动 WebView（微信 X5 / iOS WKWebView）下 vue-router scrollBehavior 不可靠，进页自证回顶
    window.scrollTo(0, 0)
    this.loadHistory()
    this.fetchHotWords()
    this.fetchTagStats() // 布尔命中状态，与字典并行（失败=全部正常态展示，不影响可用性）
    await this.fetchFilters()
    this.restoreSearchContext()
    // 自动 focus 仅 direct_search / suggestion 来源（用户点击进来的手势栈内，Chrome 直弹键盘；
    // iOS/微信 WebView 非手势 focus 不弹键盘但光标进入——"浏览器允许时"）。preventScroll 防 iOS focus 视口跳动。
    if (this.entrySource === 'direct_search' || this.entrySource === 'suggestion') {
      this.$nextTick(() => {
        if (this.$refs.keyContent) {
          this.$refs.keyContent.focus({ preventScroll: true })
        }
      })
    }
  },
  methods: {
    // Tab 切换
    switchTab (tab) {
      this.currentTab = tab
    },

    // 搜索相关方法
    loadHistory () {
      try {
        const history = localStorage.getItem(HISTORY_KEY)
        if (history) {
          this.historyWord = JSON.parse(history)
        }
      } catch (e) {
        this.historyWord = []
      }
    },
    saveHistory () {
      try {
        localStorage.setItem(HISTORY_KEY, JSON.stringify(this.historyWord))
      } catch (e) {
        console.error('保存历史搜索失败:', e)
      }
    },
    addToHistory (keyword) {
      if (!keyword || keyword.trim() === '') return
      const trimmedKeyword = keyword.trim()
      this.historyWord = this.historyWord.filter(h => h.keyword !== trimmedKeyword)
      this.historyWord.unshift({ keyword: trimmedKeyword, timestamp: Date.now() })
      if (this.historyWord.length > MAX_HISTORY) {
        this.historyWord = this.historyWord.slice(0, MAX_HISTORY)
      }
      this.saveHistory()
    },
    deleteHistory (index) {
      this.historyWord.splice(index, 1)
      this.saveHistory()
    },
    clearHistory () {
      if (!confirm('确定要清空历史搜索吗？')) return
      this.historyWord = []
      this.saveHistory()
    },
    // 拉取搜索筛选字典（车型+规格），按品类字段的 tagType 填充 options（与后台 product_tags 同源）
    async fetchFilters () {
      try {
        const res = await this.axios({ url: 'search/filters', method: 'get' })
        if (res.data.code === 200 && res.data.data) {
          const dict = res.data.data
          // 读取分类列表
          this.vehicleCategories = dict._categories || []
          // 构建 category_id → name 映射
          const catMap = {}
          this.vehicleCategories.forEach(c => { catMap[c.id] = c.name })
          // fallback: 无分类或查不到的归入"摩托车"
          const defaultCatName = '摩托车'
          // 车型列表从后端 product_tags(tag_type=model) 加载
          if (dict.model && dict.model.length > 0) {
            this.carTypes = dict.model.map(t => ({
              key: t.key || t.value,
              name: t.label || t.value,
              brand: t.key ? t.key.split('_')[0] : '',
              category: catMap[t.category_id] || defaultCatName,
              categoryId: t.category_id || null,
              image: t.image || null
            }))
          }
          // 规格选项
          this.categories.forEach(cat => {
            cat.fields.forEach(field => {
              if (field.tagType && dict[field.tagType]) {
                field.options = dict[field.tagType]
              }
            })
          })
        }
      } catch (err) {
        console.error('获取筛选字典失败:', err)
      }
    },
    restoreSearchContext () {
      const q = this.$route.query
      if (!q.tab) return

      this.currentTab = q.tab

      // 恢复规格搜索上下文：新协议 category=<key> + spec=<字段集 JSON>；
      // 旧协议（spec JSON 整包含 category/categoryName）兼容提取
      if (q.tab === 'spec') {
        let catKey = q.category
        const fields = {}
        if (q.spec) {
          try {
            const parsed = JSON.parse(q.spec)
            if (parsed && typeof parsed === 'object') {
              if (!catKey && parsed.category) catKey = parsed.category
              Object.keys(parsed).forEach(k => {
                if (k !== 'category' && k !== 'categoryName') fields[k] = parsed[k]
              })
            }
          } catch (e) {
            console.error('恢复规格搜索上下文失败:', e)
          }
        }
        const cat = this.categories.find(c => c.key === catKey)
        if (cat) {
          this.selectedCategory = cat
          this.specParams = {}
          Object.keys(fields).forEach(k => {
            this.$set(this.specParams, k, fields[k])
          })
        }
      }

      // 恢复车型搜索上下文
      if (q.tab === 'car' && q.car) {
        try {
          const car = JSON.parse(q.car)
          // carTypes 可能还未加载（fetchFilters 已 await），直接使用
          const found = this.carTypes.find(t => t.key === car.type)
          this.selectedCarType = found || car
        } catch (e) {
          console.error('恢复车型搜索上下文失败:', e)
        }
      }
    },
    async fetchHotWords () {
      try {
        const res = await this.axios({
          url: 'search/hot',
          method: 'get',
          params: { limit: 10 }
        })
        if (res.data.code === 200 && Array.isArray(res.data.data)) {
          // 统一格式：[{keyword, heat}, ...]；空数组=隐藏热搜区块
          this.hotWord = res.data.data.filter(x => x && x.keyword)
        }
      } catch (err) {
        // 热搜关闭/接口异常 → 空（不写死兜底，区块隐藏）
        this.hotWord = []
      }
    },
    formatCount (count) {
      if (count > 10000) return (count / 10000).toFixed(1) + '万'
      return count
    },

    // 快速搜索（猜你想搜）：类目入口进规格筛选上下文（不走关键词搜索、不记搜索历史）
    quickSearch (part) {
      if (part && part.category) {
        this.$router.push({ path: '/result', query: specResultQuery({ category: part.category, categoryName: part.name }) })
        return
      }
      const keyword = part && (part.keyword || part.name)
      if (!keyword) return
      this.addToHistory(keyword)
      this.$router.push({ path: '/result', query: { id: keyword, type: 'quick' } })
    },

    // 统一"词 → 搜索入口"分流：类目词（精确命中类目字典/别名）进规格筛选上下文，
    // 自由词（车型 CG125 / 品牌 / 编码 / 商品名）走关键词全能力搜索。
    // 联想/热搜/历史三类词条入口共用；输入框手动回车/搜索按钮是用户显式搜索，不经此分流
    enterSearchWord (keyword, remember) {
      const kw = String(keyword || '').trim()
      if (!kw) return
      const cat = matchSpecCategory(kw)
      if (cat) {
        this.$router.push({ path: '/result', query: specResultQuery({ category: cat.key, categoryName: kw }) })
        return
      }
      if (remember) this.addToHistory(kw)
      this.$router.push({ path: '/result', query: { id: kw } })
    },

    // 历史/热搜点击
    clickHisWord (index) {
      this.enterSearchWord(this.historyWord[index].keyword, false)
    },
    clickHotWord (index) {
      this.enterSearchWord(this.hotWord[index].keyword, true)
    },

    // 输入框方法
    removeKey () {
      this.realValue = ''
      this.$refs.keyContent.focus()
      this.isRemove = false
      this.suggestions = []
      this.showSuggest = false
    },
    getMonitor () {
      this.isRemove = this.realValue !== '' && this.realValue !== null
      // 联想词 debounce 300ms（输入停顿后请求，避免逐键打后端）
      clearTimeout(this._suggestTimer)
      this._suggestTimer = setTimeout(() => this.fetchSuggestions(), 300)
    },
    // 联想词：历史购买 + 画像偏好 + 热门三来源（后端合并去重）。输入为空则收起。
    async fetchSuggestions () {
      const q = (this.realValue || '').trim()
      if (q.length < 1) { this.suggestions = []; this.showSuggest = false; return }
      try {
        const token = localStorage.getItem('token')
        const headers = token ? { Authorization: `Bearer ${token}` } : {}
        const res = await this.axios.get('/search/suggest', { params: { q }, headers, timeout: 3000 })
        if (res.data.code === 200) {
          this.suggestions = res.data.data || []
          this.showSuggest = this.suggestions.length > 0
        }
      } catch (e) { /* 静默，不打断输入 */ }
    },
    onInputFocus () {
      if (this.suggestions.length) this.showSuggest = true
    },
    onInputBlur () {
      // 延迟收起，给 @mousedown.prevent 的 pickSuggestion 让出执行窗口
      clearTimeout(this._blurTimer)
      this._blurTimer = setTimeout(() => { this.showSuggest = false }, 150)
    },
    pickSuggestion (text) {
      this.realValue = text
      this.isRemove = true
      this.showSuggest = false
      this.enterSearchWord(text, true)
    },
    backClick () {
      this.$router.push('/home')
    },
        async onScanResult (decodedText) {
      this.showScanner = false
      const code = (decodedText || '').trim()
      console.log('[Scan] raw:', JSON.stringify(decodedText), 'cleaned:', code)
      if (!code) return
      try {
        const res = await this.axios.get('/search/products/code', { params: { code } })
        const p = res.data?.data?.product
        if (res.data.code === 200 && p) {
          this.$router.push({ path: '/details', query: { id: p.id } })
        } else {
          alert('未找到对应商品: ' + code)
        }
      } catch (err) {
        alert('查询失败，请重试')
      }
    },
    toResult () {
      // 优先用户真实输入，其次首页推荐词 placeholder
      const keyword = this.realValue?.trim() || this.placeholderKeyword?.trim()
      if (!keyword) {
        this.$toast({ message: '请输入搜索内容', position: 'top', duration: 1500 })
        return
      }
      this.addToHistory(keyword)
      this.$router.push({ path: '/result', query: { id: keyword } })
    },

    // 按规格搜索
    selectCategory (cat) {
      this.selectedCategory = cat
      this.specParams = {}
    },
    selectSpec (fieldKey, value) {
      this.$set(this.specParams, fieldKey, value)
    },
    // 拉取规格标签命中状态（布尔版：品类下该选项是否有匹配商品，与搜索命中同源）
    async fetchTagStats () {
      try {
        const res = await this.axios({ url: 'search/tag-stats', method: 'get' })
        if (res.data.code === 200 && res.data.data) {
          this.tagStats = res.data.data.has || {}
          this.tagStatsLoaded = true
        }
      } catch (err) {
        console.error('获取标签命中状态失败:', err)
      }
    },
    // 有效标签预选（SEARCH_SPEC_PRESELECT）：只展示当前品类可产生搜索结果的选项；
    // 排序=后端命中数降序（tagStats 键序，无数量出网）。
    // 统计未到=字典原序全展示（保守不误杀）；统计已到但字段无键=品类内 0 命中=整字段不渲染
    fieldOptions (field) {
      if (!field || !field.options) return []
      if (!this.tagStatsLoaded) return field.options
      const cat = this.selectedCategory && this.tagStats[this.selectedCategory.key]
      const vals = cat && cat[field.key]
      if (!vals) return []
      const rank = {}
      Object.keys(vals).forEach((v, i) => { rank[v] = i })
      return field.options.filter(o => vals[o.value])
        .sort((a, b) => rank[a.value] - rank[b.value])
    },
    visibleOptions (field) {
      const opts = this.fieldOptions(field)
      return this.specFieldExpanded[field.key] ? opts : opts.slice(0, DEFAULT_VISIBLE_OPTIONS)
    },
    toggleField (key) {
      this.$set(this.specFieldExpanded, key, !this.specFieldExpanded[key])
    },
    searchBySpec () {
      // 新协议：category/categoryName 独立参数，spec=已选字段集（空=类目默认全量，不带脏参数）
      this.$router.push({
        path: '/result',
        query: specResultQuery({
          category: this.selectedCategory.key,
          categoryName: this.selectedCategory.name,
          spec: this.specParams
        })
      })
    },

    // 按品牌分组
    groupByBrand (category) {
      const types = this.carTypes.filter(t => t.category === category)
      return types.reduce((acc, type) => {
        if (!acc[type.brand]) {
          acc[type.brand] = []
        }
        acc[type.brand].push(type)
        return acc
      }, {})
    },

    // 按车型搜索
    selectCarType (type) {
      this.selectedCarType = type
    },
    searchByCar () {
      if (!this.selectedCarType) return
      const carQuery = {
        type: this.selectedCarType.key,
        name: this.selectedCarType.name,
        category: this.selectedCarType.category,
        categoryId: this.selectedCarType.categoryId
      }
      this.$router.push({
        path: '/result',
        query: {
          type: 'car',
          car: JSON.stringify(carQuery)
        }
      })
    }
  }
}
